<!--
  Lists the global objects
  Similar to SkillPage
-->

<template>
  <div v-loading="state.loading">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item>
        <router-link :to="{ name: 'home' }">
          Home
        </router-link>
      </el-breadcrumb-item>
      <el-breadcrumb-item>Global items</el-breadcrumb-item>
    </el-breadcrumb>
    <main-title>Global items</main-title>
    <view-selector
      :view="view"
      :compare="compare"
      @update="changeView"
      :force="true"></view-selector>
    <!-- entities -->
    <div>
      <subtitle id="entities">
        <template slot="tooltip">
          <em>Entities</em> can match specific words and translate them to <em>variables</em>.<br/>
          They can be used in <em>intents</em> with the syntax <code>@myvariable</code>
        </template>Entities</subtitle>
      <!-- existing entities -->
      <el-tree class="global-list"
        :data="listEntities"
        node-key="entity"
        :default-expanded-keys="expanded.entities"
        v-if="listEntities.length">
        <template v-slot="{ data: entity }">
          <div class="global-list-item">
            <span v-if="entity.type==='node'"
              class="global-list-main">
              <span class="global-list-count"
                >&lt;{{ entity.children.length }}&gt;</span>
              <span class="global-list-node global-list-name">
                {{entity.shortName}}
              </span>
            </span>
            <span v-else class="global-list-main"
              v-id="'entities-' + entity.entity">
              <span class="global-list-count"
                >({{ entity.first.count || '-' }})</span>
              <el-tooltip placement="top" effect="light" :enterable="false" :tabindex="-1">
                <i :class="entity.first.status==='deleted'?'el-icon-circle-close':entity.first.status==='created'?'el-icon-circle-plus-outline':entity.first.status==='edited'?'el-icon-warning-outline':'el-icon-circle-check'"></i>
                <template slot="content">
                  {{entity.first.explanation}}
                </template>
              </el-tooltip>
              <!-- view version (or compare if not in view) -->
              <router-link
                :to="{name: 'entity', params: {skill: 'global', entity: entity.entity}, query: entity.query}"
                class="global-list-intent global-list-name"
                :class="'global-list-' + entity.first.source"
                >{{entity.shortName}}</router-link>
              <!-- compare version if different from view -->
              <router-link v-if="entity.second"
                :to="{name: 'entity', params: {skill: 'global', entity: entity.entity}, query: entity.query}"
                class="global-list-intent global-list-name"
                :class="'global-list-' + entity.second.source">{{
                  shortenName(entity.prefix, entity.second.name)
                }}</router-link>
            </span>
          </div>
        </template>
      </el-tree>
      <!-- new entity -->
      <div class="intent-list">
        <div class="intent-list-item" style="margin-top: 0.5em"
          v-if="!view">
          <span class="global-list-main">
            <span class="global-list-count"
              >&nbsp;+</span>
            <router-link
              :to="{name: 'entity', params: {skill: 'global', entity: 'new'}}"
              class="global-list-intent global-list-add"
              >New entity</router-link>
          </span>
        </div>
      </div>
    </div>
    <!-- synonyms -->
    <div>
      <subtitle id="synonyms">
        <template slot="tooltip">
          <em>Synonyms</em> help intents to be more versatile.<br/>
          They can be used in <em>intents</em> with the syntax <code>#mysynonym</code>
        </template>Synonyms</subtitle>
      <!-- existing synonyms -->
      <el-tree class="global-list"
        :data="listSynonyms"
        node-key="synonym"
        :default-expanded-keys="expanded.synonyms"
        v-if="listSynonyms.length">
        <template v-slot="{ data: synonym }">
          <div class="global-list-item">
            <span v-if="synonym.type==='node'"
              class="global-list-main">
              <span class="global-list-count"
                >&lt;{{ synonym.children.length }}&gt;</span>
              <span class="global-list-node global-list-name">
                {{synonym.shortName}}
              </span>
            </span>
            <span v-else class="global-list-main"
              v-id="'synonyms-' + synonym.synonym">
              <span class="global-list-count"
                >({{ synonym.first.count || '-' }})</span>
              <el-tooltip placement="top" effect="light" :enterable="false" :tabindex="-1">
                <i :class="synonym.first.status==='deleted'?'el-icon-circle-close':synonym.first.status==='created'?'el-icon-circle-plus-outline':synonym.first.status==='edited'?'el-icon-warning-outline':'el-icon-circle-check'"></i>
                <template slot="content">
                  {{synonym.first.explanation}}
                </template>
              </el-tooltip>
              <!-- view version (or compare if not in view) -->
              <router-link
                :to="{name: 'synonym', params: {skill: 'global', synonym: synonym.synonym}, query: synonym.query}"
                class="global-list-intent global-list-name"
                :class="'global-list-' + synonym.first.source"
                >{{synonym.shortName}}</router-link>
              <!-- compare version if different from view -->
              <router-link v-if="synonym.second"
                :to="{name: 'synonym', params: {skill: 'global', synonym: synonym.synonym}, query: synonym.query}"
                class="global-list-intent global-list-name"
                :class="'global-list-' + synonym.second.source">{{
                  shortenName(synonym.prefix, synonym.second.name)
                }}</router-link>
            </span>
          </div>
        </template>
      </el-tree>
      <!-- new synonyms -->
      <div class="global-list">
        <div class="global-list-item" style="margin-top: 0.5em"
          v-if="!view">
          <span class="global-list-main">
            <span class="global-list-count"
              >&nbsp;+</span>
            <router-link
              :to="{name: 'synonym', params: {skill: 'global', synonym: 'new'}}"
              class="global-list-intent global-list-add"
              >New synonym</router-link>
          </span>
        </div>
      </div>
    </div>
    <!-- scripts -->
    <div>
      <subtitle id="scripts">
        <template slot="tooltip">
          <em>Python scripts</em> define functions that can then be used in <em>script actions</em>.
        </template>Scripts</subtitle>
      <!-- existing scripts -->
      <el-tree class="global-list"
        :data="listScripts"
        node-key="script"
        :default-expanded-keys="expanded.scripts"
        v-if="listScripts.length">
        <template v-slot="{ data: script }">
          <div class="global-list-item">
            <span v-if="script.type==='node'"
              class="global-list-main">
              <span class="global-list-count"
                >&lt;{{ script.children.length }}&gt;</span>
              <span class="global-list-node global-list-name">
                {{script.shortName}}
              </span>
            </span>
            <span v-else class="global-list-main"
              v-id="'scripts-' + script.script">
              <span class="global-list-count"
                >({{ script.first.count || '-' }})</span>
              <el-tooltip placement="top" effect="light" :enterable="false" :tabindex="-1">
                <i :class="script.first.status==='deleted'?'el-icon-circle-close':script.first.status==='created'?'el-icon-circle-plus-outline':script.first.status==='edited'?'el-icon-warning-outline':'el-icon-circle-check'"></i>
                <template slot="content">
                  {{script.first.explanation}}
                </template>
              </el-tooltip>
              <!-- view version (or compare if not in view) -->
              <router-link
                :to="{name: 'script', params: {skill: 'global', script: script.script}, query: script.query}"
                class="global-list-intent global-list-name"
                :class="'global-list-' + script.first.source"
                >{{script.shortName}}</router-link>
              <!-- compare version if different from view -->
              <router-link v-if="script.second"
                :to="{name: 'script', params: {skill: 'global', script: script.script}, query: script.query}"
                class="global-list-intent global-list-name"
                :class="'global-list-' + script.second.source">{{
                  shortenName(script.prefix, script.second.name)
                }}</router-link>
            </span>
          </div>
        </template>
      </el-tree>
      <!-- new scripts -->
      <div class="global-list">
        <div class="global-list-item" style="margin-top: 0.5em"
          v-if="!view">
          <span class="global-list-main">
            <span class="global-list-count"
              >&nbsp;+</span>
            <router-link
              :to="{name: 'script', params: {skill: 'global', script: 'new'}}"
              class="global-list-intent global-list-add"
              >New script</router-link>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import State from '../../stores/state.js'
import { comparedItems, buildTree } from '../../utils/compare.js'
import ViewSelector from '../ViewSelector.vue'

export default {
  components: {
    ViewSelector,
  },
  props: {
    view: String,
    compare: String,
  },
  data () {
    return {
      state: State.state,
      viewObj: State.empty,
      compareObj: { data: null },
      expanded: { // what should be extended, due to anchor links
        entities: [],
        synonyms: [],
        scripts: [],
      },
    }
  },
  computed: {
    // shortcut for the data
    value () { return this.viewObj.data },
    other () { return this.compareObj.data },
    // the list of all entities, compared, as a tree
    listEntities () {
      // see utils/compare.js
      let entities = comparedItems('entity',
        this.value.entities,
        this.other && this.other.entities,
        this.$route.query)

      let data = []
      for (let entity in entities) {
        data.push(entities[entity])
      }
      return buildTree(data, true)
    },
    // the list of all synonyms, compared, as a tree
    listSynonyms () {
      // see utils/compare.js
      let synonyms = comparedItems('synonym',
        this.value.synonyms,
        this.other && this.other.synonyms,
        this.$route.query)

      let data = []
      for (let synonym in synonyms) {
        data.push(synonyms[synonym])
      }
      return buildTree(data, true)
    },
    // the list of all scripts, compared, as a tree
    listScripts () {
      // see utils/compare.js
      let scripts = []
      for (let script of this.value.scripts || []) {
        scripts.push({
          ...script,
          name: script.module + '.' + script.function,
        })
      }
      scripts = comparedItems('script',
        scripts,
        this.other && this.other.scripts,
        this.$route.query)

      let data = []
      for (let script in scripts) {
        data.push(scripts[script])
      }
      return buildTree(data, true)
    },
  },
  inject: ['onerror'],
  // load global object list
  created () {
    this.loadView()
    this.loadCompare()
  },
  watch: {
    // user connected, reload everything
    'state.user' () {
      this.loadView()
      this.loadCompare()
    },
    // reload view
    view () { this.loadView() },
    // reload compare
    compare () { this.loadCompare() },
  },
  methods: {
    // load view global objects
    loadView () {
      let view = this.view
      State.getSkill('global', view, true)
        .then(data => {
          if (view !== this.view) return
          this.viewObj = data
        }).catch(this.onerror)
    },
    // load compare global objects
    loadCompare () {
      let view = this.compare
      if (!view) {
        this.compareObj = { data: null }
        return
      } else {
        State.getSkill('global', view, true)
          .then(data => {
            if (view !== this.compare) return
            this.compareObj = data
          }).catch(this.onerror)
      }
    },
    // updte view and compare
    changeView (view, compare) {
      this.$router.push({ query: { view: view, compare: compare } })
    },
    // expand tree items if they match the URL hash
    expandFromHash () {
      let hash = this.$route.hash
      hash = hash && hash.substr(1).split('-')
      if (!hash || hash.length !== 2 || hash[1].length !== 32 ||
        !this.expanded[hash[0]]) return
      this.expanded[hash[0]] = [hash[1]]
    },
    shortenName (prefix, name) {
      if (prefix && name.substr(0, prefix.length) === prefix) {
        return '…' + name.substr(prefix.length)
      } else return name
    },
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";

  .global-list-intent {
    color: $--color-text-regular;
    /*font-weight: bold;*/
    text-decoration: none;
    margin-left: 5px;
  }
  .global-list-intent:hover {
    text-decoration: underline;
  }
  .global-list-name {
    margin-left: 5px
  }
  .global-list-add {
    font-style: italic;
  }
  .global-list-new {
    background-color: $--color-primary-lighter;
  }
  .global-list-old {
    background-color: $--color-warning-lighter;
  }
  .global-list-main {
    display: inline-block;
    min-width: 200px;
    margin-left: -200px;

    .el-icon-circle-check {
      color: $--color-success;
    }
    .el-icon-circle-plus-outline {
      color: $--color-primary;
    }
    .el-icon-warning-outline {
      color: $--color-primary;
    }
    .el-icon-circle-close {
      color: $--color-warning;
    }
  }
  .global-list-infos {
    .el-icon-error {
      color: $--color-danger;
    }
  }
  .global-list-count {
    font-family: monospace;
    display: inline-block;
    width: 32px;
  }
  .global-list, .el-tree.global-list {
    color: $--color-text-placeholder;
    padding-left: 40px;
  }
  .global-list .el-tree-node__content {
    height: auto;
    cursor: default;
    > .el-tree-node__expand-icon {
      padding: 0 6px;
      margin-left: -24px;
    }
  }
  .global-list .el-tree-node__children {
    margin-left: 16px;
  }
  .global-list .global-list-item {
    margin-left: 200px;
    &:hover, &:hover > * {
      background-color: $--background-color-base;
    }
  }
</style>
